import type { LucideIcon } from "lucide-react";
import {
  FileQuestion,
  LockKeyhole,
  LogIn,
  RefreshCw,
  ServerCrash,
  ShieldAlert,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";

import { Button } from "@evomatic-ui-kit/button";
import { Card, CardContent } from "@evomatic-ui-kit/card";

interface Props {
  status?: number;
  error?: unknown;
  reset?: () => void;
  variant?: "unavailable";
  isRetrying?: boolean;
  onRetry?: () => void;
}

const accentClasses: Record<
  "primary" | "destructive",
  { badge: string; ring: string; blob: string; glow: string }
> = {
  primary: {
    badge: "bg-primary/10 text-primary",
    ring: "ring-primary/10",
    blob: "bg-primary",
    glow: "shadow-primary/20 hover:shadow-primary/30",
  },
  destructive: {
    badge: "bg-destructive/10 text-destructive",
    ring: "ring-destructive/10",
    blob: "bg-destructive",
    glow: "shadow-destructive/20 hover:shadow-destructive/30",
  },
};

const errorConfig: Record<
  401 | 403 | 404 | 500,
  {
    Icon: LucideIcon;
    accent: keyof typeof accentClasses;
  }
> = {
  401: {
    Icon: LockKeyhole,
    accent: "destructive",
  },
  403: {
    Icon: ShieldAlert,
    accent: "destructive",
  },
  404: {
    Icon: FileQuestion,
    accent: "primary",
  },
  500: {
    Icon: ServerCrash,
    accent: "destructive",
  },
};

const Error = ({
  status: statusProp,
  variant,
  isRetrying = false,
  onRetry
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // no status is passed when this renders as a route's errorComponent, so fall back to the generic 500 view
  const statusCode =
    statusProp && statusProp in errorConfig ? (statusProp as 401 | 403 | 404 | 500) : 500;
  const { Icon, accent } = errorConfig[statusCode];
  const { badge, ring, blob } = accentClasses[accent];
  const translationKey = variant === "unavailable" ? "unavailable" : statusCode;

  const handleGoToLogin = async () => {
    await navigate({ to: "/login" });
  };

  return (
    <div className="relative flex h-screen w-screen items-center justify-center overflow-hidden bg-background p-6">
      <div
        aria-hidden
        className={`absolute -top-32 -left-32 size-96 rounded-full opacity-20 blur-3xl ${blob}`}
      />
      <div
        aria-hidden
        className={`absolute -right-32 -bottom-32 size-96 rounded-full opacity-20 blur-3xl ${blob}`}
      />

      <Card className="animate-in fade-in zoom-in-95 relative w-full max-w-md overflow-hidden rounded-3xl border-none py-0 shadow-2xl duration-500">
        <span
          aria-hidden
          className="pointer-events-none absolute -top-8 -right-4 text-[9rem] leading-none font-black text-muted-foreground/5 select-none"
        >
          {statusCode}
        </span>
        <CardContent className="relative flex flex-col items-center gap-0 px-8 pt-12 pb-10 text-center">
          <div
            className={`flex size-20 items-center justify-center rounded-2xl ring-8 ${badge} ${ring}`}
          >
            <Icon className="size-9" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight mt-6">
            {t(`common.error.${translationKey}.title`)}
          </h1>
          <p className="max-w-xs text-sm text-muted-foreground mt-2">
            {t(`common.error.${translationKey}.description`)}
          </p>
          {variant === "unavailable" && onRetry ? (
            <Button
              size="lg"
              className="mt-8 gap-2 transition-all hover:scale-105 cursor-pointer"
              disabled={isRetrying}
              onClick={onRetry}
            >
              <RefreshCw className={`size-4 ${isRetrying ? "animate-spin" : ""}`} />
              {t("common.retry")}
            </Button>
          ) : (
            <Button
              size="lg"
              className="mt-8 gap-2 transition-all hover:scale-105 cursor-pointer"
              onClick={() => void handleGoToLogin()}
            >
              <LogIn className="size-4" />
              {t("common.goto_login")}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Error;
